<template>
  <Teleport to="body">
    <Transition name="pip-pop">
      <div
        v-if="isVideoActive"
        ref="containerRef"
        class="fixed z-[9990] bg-black rounded-xl overflow-hidden shadow-2xl border border-gray-700 flex flex-col"
        :style="containerStyle">
        <div
          class="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-900 cursor-grab active:cursor-grabbing select-none shrink-0"
          @mousedown="startDrag"
          @dblclick="player.showVideoPopup.value = !player.showVideoPopup.value">
          <div class="flex items-center gap-2 min-w-0">
            <span class="material-symbols-outlined text-sm text-gray-400">movie</span>
            <span class="text-xs text-gray-300 truncate">{{ player.currentItem.value?.title }}</span>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button
              @click.stop="player.showVideoPopup.value = !player.showVideoPopup.value"
              class="text-gray-400 hover:text-white p-0.5 transition-colors"
              :title="player.showVideoPopup.value ? tt('Collapse', 'Strni') : tt('Expand', 'Razširi')">
              <span class="material-symbols-outlined text-sm">
                {{ player.showVideoPopup.value ? 'keyboard_arrow_down' : 'open_in_full' }}
              </span>
            </button>
            <button
              @click.stop="player.closeBrowserPlayer()"
              class="text-gray-400 hover:text-white p-0.5 transition-colors"
              :title="tt('Close', 'Zapri')">
              <span class="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        </div>

        <div class="relative flex-1 bg-black min-h-0">
          <video
            :ref="(el) => player.registerVideoEl(el as HTMLVideoElement | null)"
            :src="player.browserSrc.value"
            class="bg-black"
            :class="
              player.showVideoPopup.value
                ? 'w-full h-full object-contain'
                : 'absolute left-0 top-0 h-px w-px opacity-0 pointer-events-none'
            "
            @timeupdate="player.onTimeUpdate"
            @loadedmetadata="player.onMetadata"
            @ended="player.onEnded"
            @play="player.browserPlaying.value = true"
            @pause="player.browserPlaying.value = false"
            controls
            preload="metadata" />
        </div>

        <div
          v-if="player.showVideoPopup.value"
          class="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-10"
          @mousedown.stop="startResize"
          :title="tt('Resize', 'Spremeni velikost')">
          <svg class="w-full h-full text-gray-600" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="8" cy="12" r="1.5" />
            <circle cx="12" cy="8" r="1.5" />
          </svg>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useBrowserPlayer } from '@/composables/useBrowserPlayer';
import { lockDragTextSelection } from '@/utils/dragSelectionGuard';
import { tt } from '../../utils/consoleText';

interface VideoPipPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

const STORAGE_KEY = 'otto-browser-video-pip';
const MIN_WIDTH = 260;
const MIN_HEIGHT = 170;
const COLLAPSED_HEIGHT = 40;
const COLLAPSED_WIDTH = 320;

const player = useBrowserPlayer();
const containerRef = ref<HTMLElement | null>(null);
const position = ref<VideoPipPosition>(loadPosition());

const isVideoActive = computed(() => player.currentItem.value?.mediaType === 'video');

const containerStyle = computed(() => {
  const collapsed = !player.showVideoPopup.value;
  return {
    left: `${position.value.x}px`,
    top: `${position.value.y}px`,
    width: `${collapsed ? Math.max(MIN_WIDTH, Math.min(position.value.w, COLLAPSED_WIDTH)) : position.value.w}px`,
    height: `${collapsed ? COLLAPSED_HEIGHT : position.value.h}px`,
  };
});

function defaultPosition(): VideoPipPosition {
  if (typeof window === 'undefined') {
    return { x: 24, y: 80, w: 420, h: 280 };
  }

  const w = Math.min(420, Math.max(MIN_WIDTH, window.innerWidth - 32));
  const h = Math.round((w * 9) / 16) + 38;
  return {
    x: Math.max(16, window.innerWidth - w - 24),
    y: Math.max(64, window.innerHeight - h - 96),
    w,
    h,
  };
}

function loadPosition(): VideoPipPosition {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return clampPosition({ ...defaultPosition(), ...JSON.parse(raw) });
    }
  } catch {
    // Ignore corrupt persisted PiP state.
  }
  return clampPosition(defaultPosition());
}

function savePosition(nextPosition: VideoPipPosition) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextPosition));
  } catch {
    // Ignore storage failures.
  }
}

function clampPosition(nextPosition: VideoPipPosition): VideoPipPosition {
  if (typeof window === 'undefined') {
    return nextPosition;
  }

  const w = Math.max(MIN_WIDTH, Math.min(nextPosition.w, Math.max(MIN_WIDTH, window.innerWidth - 16)));
  const h = Math.max(MIN_HEIGHT, Math.min(nextPosition.h, Math.max(MIN_HEIGHT, window.innerHeight - 16)));
  const maxX = Math.max(0, window.innerWidth - Math.min(w, COLLAPSED_WIDTH));
  const maxY = Math.max(0, window.innerHeight - COLLAPSED_HEIGHT);

  return {
    x: Math.max(0, Math.min(nextPosition.x, maxX)),
    y: Math.max(0, Math.min(nextPosition.y, maxY)),
    w,
    h,
  };
}

function updatePosition(patch: Partial<VideoPipPosition>) {
  position.value = clampPosition({ ...position.value, ...patch });
  savePosition(position.value);
}

function ensureInViewport() {
  updatePosition({});
}

let dragOffset = { x: 0, y: 0 };
let isDragging = false;
let releaseSelectionLock: (() => void) | null = null;

function startSelectionLock() {
  releaseSelectionLock?.();
  releaseSelectionLock = lockDragTextSelection();
}

function stopSelectionLock() {
  releaseSelectionLock?.();
  releaseSelectionLock = null;
}

function startDrag(event: MouseEvent) {
  if (event.button !== 0 || (event.target as HTMLElement).closest('button')) return;

  event.preventDefault();
  isDragging = true;
  startSelectionLock();
  dragOffset = {
    x: event.clientX - position.value.x,
    y: event.clientY - position.value.y,
  };
  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
}

function onDrag(event: MouseEvent) {
  if (!isDragging) return;
  updatePosition({
    x: event.clientX - dragOffset.x,
    y: event.clientY - dragOffset.y,
  });
}

function stopDrag() {
  isDragging = false;
  stopSelectionLock();
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
}

let isResizing = false;

function startResize(event: MouseEvent) {
  if (event.button !== 0) return;

  event.preventDefault();
  isResizing = true;
  startSelectionLock();
  window.addEventListener('mousemove', onResize);
  window.addEventListener('mouseup', stopResize);
}

function onResize(event: MouseEvent) {
  if (!isResizing) return;
  updatePosition({
    w: event.clientX - position.value.x,
    h: event.clientY - position.value.y,
  });
}

function stopResize() {
  isResizing = false;
  stopSelectionLock();
  window.removeEventListener('mousemove', onResize);
  window.removeEventListener('mouseup', stopResize);
}

watch(isVideoActive, (active) => {
  if (active) ensureInViewport();
});

onMounted(() => {
  window.addEventListener('resize', ensureInViewport);
});

onUnmounted(() => {
  window.removeEventListener('resize', ensureInViewport);
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
  window.removeEventListener('mousemove', onResize);
  window.removeEventListener('mouseup', stopResize);
  stopSelectionLock();
});
</script>

<style scoped>
.pip-pop-enter-active,
.pip-pop-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.pip-pop-enter-from,
.pip-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(10px);
}
</style>
